import { db, type SyncEntity, type OutboxOperation, type Syncable } from './database'

/**
 * Moteur de synchronisation outbox.
 *
 * Protections en place :
 *  - id UUIDv7 client + batch_id stable   -> aucun doublon même si la réponse se perd
 *  - statut "in_flight"                   -> reprise si l'app est tuée en plein envoi
 *  - envoi dans l'ordre d'insertion       -> les dépendances passent avant leurs enfants
 *  - numéro de version par ligne          -> les conflits sont détectés, pas écrasés
 *  - compteur de tentatives               -> une opération refusée ne bloque pas la file
 *  - pull paginé, repris au checkpoint    -> un rattrapage coupé ne perd rien
 *
 * Reste à faire avant la production : compression des lots, purge des tombstones
 * anciens, rotation du jeton de synchronisation.
 */

const API = import.meta.env?.VITE_API_URL ?? '/api/v1'
const MAX_ATTEMPTS = 5
const PAGE_SIZE = 200

export type SyncMode = 'online' | 'offline'

function readStorage(key: string): string | null {
  try { return localStorage.getItem(key) } catch { return null }
}
function writeStorage(key: string, value: string) {
  try { localStorage.setItem(key, value) } catch { /* navigation privée */ }
}

/** Le mode est PAR APPAREIL, pas par compte (décision vision.md). */
export function getSyncMode(): SyncMode {
  return (readStorage('ariariko:sync_mode') as SyncMode) ?? 'online'
}
export function setSyncMode(mode: SyncMode) {
  writeStorage('ariariko:sync_mode', mode)
}

export function getToken(): string | null {
  return readStorage('ariariko:token')
}
export function setToken(token: string) {
  writeStorage('ariariko:token', token)
}

function headers(): HeadersInit {
  const token = getToken()
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

function tableFor(entity: string) {
  return (db as unknown as Record<string, import('dexie').Table>)[entity]
}

function isOnline(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine
}

/**
 * Écriture locale. Retourne dès que Dexie a écrit — l'interface se met à jour
 * immédiatement. La synchro part en arrière-plan, sans jamais être attendue.
 */
export async function writeLocal<T extends { id: string }>(
  entity: SyncEntity,
  doc: T,
): Promise<void> {
  const table = tableFor(entity)
  // Horloge locale : c'est CE champ qui arbitre "dernière date gagne" côté
  // serveur. Il est posé une fois, ici, pour que la valeur envoyée soit
  // exactement celle qui est stockée en local.
  const client_updated_at = new Date().toISOString()

  await db.transaction('rw', table, db.outbox, async () => {
    const existing = (await table.get(doc.id)) as Syncable | undefined
    const base_version = existing?._version ?? 0
    const row = { ...doc, _version: base_version, client_updated_at, deleted_at: null }
    await table.put(row)
    await db.outbox.add({
      entity, id: doc.id, op: 'upsert', data: row,
      base_version, status: 'pending', attempts: 0,
    } satisfies OutboxOperation)
  })

  // sync-on-write : on ne l'attend JAMAIS (règle d'architecture vision.md)
  if (getSyncMode() === 'online' && isOnline()) pushInBackground()
}

/**
 * Suppression locale — un tombstone, jamais un delete.
 *
 * Supprimer physiquement la ligne rendrait la suppression invisible aux autres
 * appareils : ils la reverraient réapparaître au pull suivant. Toute lecture
 * métier doit donc filtrer sur `deleted_at`.
 */
export async function deleteLocal(entity: SyncEntity, id: string): Promise<void> {
  const table = tableFor(entity)
  const deleted_at = new Date().toISOString()

  await db.transaction('rw', table, db.outbox, async () => {
    const existing = (await table.get(id)) as Syncable | undefined
    if (!existing || existing.deleted_at) return
    const row = { ...existing, deleted_at, client_updated_at: deleted_at }
    await table.put(row)
    await db.outbox.add({
      entity, id, op: 'delete', data: row,
      base_version: existing._version ?? 0, status: 'pending', attempts: 0,
    } satisfies OutboxOperation)
  })

  if (getSyncMode() === 'online' && isOnline()) pushInBackground()
}

/**
 * Synchronisation d'arrière-plan, lancée sans être attendue.
 *
 * L'échec est un état NORMAL ici, pas une anomalie : hors ligne, `fetch` lève,
 * et les opérations restent simplement dans la file pour la prochaine
 * tentative. Sans ce `catch`, chaque écriture hors ligne produirait un rejet de
 * promesse non traité — c'est-à-dire une erreur remontée au navigateur et aux
 * outils de suivi, à chaque saisie, dans le cas d'usage le plus courant de
 * l'application.
 */
function pushInBackground() {
  void push().catch(() => { /* la file est conservée, on retentera */ })
}

let pushInProgress = false

/** Envoie les changements locaux. Idempotent, sûr à rappeler. */
export async function push(): Promise<void> {
  // Sans jeton, chaque appel part pour se faire refuser en 401. Ce n'est pas
  // seulement inutile : à Madagascar, la donnée mobile se paie, et une app qui
  // consomme du forfait pour rien est une app qu'on désinstalle. La file, elle,
  // est conservée : tout repartira à la première connexion.
  if (!getToken()) return
  if (pushInProgress) return
  pushInProgress = true
  try {
    const inFlight = await db.outbox.where('status').equals('in_flight').toArray()
    const pending = await db.outbox.where('status').equals('pending').toArray()
    const batch = [...inFlight, ...pending].filter((o) => o.attempts < MAX_ATTEMPTS)
    if (batch.length === 0) return

    // batch_id réutilisé tel quel en cas de renvoi : c'est ce qui empêche le doublon.
    const batch_id = inFlight[0]?.batch_id ?? crypto.randomUUID()
    await db.outbox.where('seq').anyOf(batch.map((o) => o.seq!))
      .modify((o) => { o.status = 'in_flight'; o.batch_id = batch_id; o.attempts += 1 })

    const response = await fetch(`${API}/sync/push`, {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify({
        batch_id,
        operations: batch.map(({ entity, id, op, base_version, data }) =>
          ({ entity, id, op, base_version, data })),
      }),
    })
    if (!response.ok) return   // les opérations restent in_flight, on retentera

    const { results } = await response.json()
    for (const result of results) {
      const table = tableFor(result.entity)
      if (result.status === 'ok') {
        await table.where('id').equals(result.id).modify({ _version: result.version })
      } else if (result.status === 'conflict') {
        // On garde LES DEUX versions, l'utilisateur tranchera.
        await db.conflicts.put({
          id: result.id, entity: result.entity,
          local_version: await table.get(result.id),
          server_version: result.server_data,
          // Le numéro de version, et pas seulement les champs : adopter la
          // version du serveur sans lui ferait repartir la ligne sur une base
          // périmée, et le prochain envoi rouvrirait le même conflit.
          server_row_version: result.version,
          detected_at: new Date().toISOString(),
        })
      }
      // `rejected` : opération invalide ou non autorisée. Elle sort de la file
      // avec le reste du lot — la garder ne ferait que la refaire échouer en boucle.
    }
    await db.outbox.where('batch_id').equals(batch_id).delete()
    // Volontairement PAS de mise à jour du checkpoint ici : il n'avance qu'au
    // pull. L'avancer sur un push ferait sauter les écritures des AUTRES
    // appareils, déjà enregistrées côté serveur mais jamais rapatriées.
    await markSynced()
  } finally {
    pushInProgress = false
  }
}

/**
 * Récupère les changements du serveur, page par page jusqu'à épuisement.
 * N'écrase jamais un travail local encore en attente d'envoi.
 */
export async function pull(): Promise<void> {
  if (!getToken()) return
  let checkpoint = Number((await db.meta.get('checkpoint'))?.value ?? 0)
  let pages = 0

  for (;;) {
    const response = await fetch(
      `${API}/sync/pull?since=${checkpoint}&limit=${PAGE_SIZE}`,
      /*
       * ⚠️ `cache: 'no-store'` — correction 2026-09-06, constatée en
       * production.
       *
       * Sans lui, le navigateur garde la réponse, la revalide au deuxième
       * appel identique, et reçoit un **304 Not Modified** (Rails pose une
       * empreinte sur toute réponse GET, via `Rack::ETag`). Or `response.ok`
       * est FAUX sur un 304 : le pull repartait sans rien écrire. Le serveur
       * renonce désormais à l'empreinte de son côté aussi — les deux, parce
       * qu'une app déjà installée continue de tourner sur l'ancien code.
       */
      { headers: headers(), cache: 'no-store' },
    )
    // 304 : plus rien à prendre. Ce n'est pas un échec — la date de dernière
    // synchronisation doit avancer quand même, sinon l'écran laisse croire
    // que rien ne fonctionne.
    if (response.status === 304) break
    if (!response.ok) {
      throw new Error(`pull refusé (${response.status})`)
    }

    const { documents, checkpoint: next, more } = await response.json()

    for (const doc of documents) {
      /*
       * ⚠️ PANNE DE PRODUCTION DU 2026-09-06 — le pull mourait ici.
       *
       * Le serveur transporte des entités que ce client ne connaît pas :
       * `wallet_members` en particulier, qu'aucune table Dexie ne peut ranger
       * (voir `SYNC_ENTITIES` dans `database.ts`). `tableFor()` renvoyait
       * `undefined`, et `.put()` levait EN PLEINE PAGE — donc avant que le
       * checkpoint n'avance. La même page échouait ensuite indéfiniment : la
       * synchronisation descendante était morte pour TOUS les comptes, dès le
       * premier pull, et rien ne le disait.
       *
       * Un serveur qui ajoute une entité ne doit jamais casser un client plus
       * ancien. Corollaire assumé : ce qui est ignoré ici ne sera pas rejoué
       * plus tard, le checkpoint passant devant. Le jour où le client saura
       * lire ces lignes (portefeuille partagé), il faudra les rattraper en
       * remettant son checkpoint à zéro.
       */
      const table = tableFor(doc.entity)
      if (!table) continue

      const pendingCount = await db.outbox.where('id').equals(doc.id).count()
      if (pendingCount > 0) continue          // ne pas écraser du local non envoyé
      await table.put({ ...doc.data, _version: doc.version })
    }

    // Le checkpoint n'est écrit qu'APRÈS les documents de la page : si l'app est
    // tuée entre les deux, la page est simplement rejouée. L'inverse perdrait
    // définitivement des lignes.
    checkpoint = Number(next)
    await db.meta.put({ key: 'checkpoint', value: checkpoint })

    if (!more) break
    if (++pages > 500) break              // garde-fou contre une pagination qui piétine
  }
  await markSynced()
}

async function markSynced() {
  await db.meta.put({ key: 'last_sync', value: new Date().toISOString() })
}

/** Le bouton du mode Offline : push puis pull, dans cet ordre. */
export async function syncNow(): Promise<void> {
  await push()
  await pull()
}

/** Avertissement à 3 jours sans synchronisation (décision vision.md). */
export async function daysSinceLastSync(): Promise<number | null> {
  const value = (await db.meta.get('last_sync'))?.value as string | undefined
  if (!value) return null
  return Math.floor((Date.now() - new Date(value).getTime()) / 86_400_000)
}

/*
 * Aller CHERCHER, et pas seulement envoyer — correction 2026-09-06.
 *
 * ⚠️ Le mode En ligne n'était « automatique » que dans un sens : chaque
 * écriture partait toute seule, mais RIEN ne descendait sans que l'utilisateur
 * n'appuie sur « Synchroniser » dans le Profil. Deux appareils du même compte
 * divergeaient donc en silence : ce qui était saisi sur l'ordinateur
 * n'atteignait jamais le téléphone. Le moment où l'on va chercher n'avait
 * simplement jamais été décidé (voir vision.md).
 *
 * Trois moments, et pas un de plus : l'ouverture, le retour du réseau, et le
 * retour dans l'app. Aucune vérification périodique — une app qui consomme du
 * forfait pendant que personne ne la regarde est une app qu'on désinstalle.
 * Le pull étant incrémental (il repart d'un checkpoint), un contrôle à vide ne
 * coûte que quelques centaines d'octets.
 */
const PULL_COOLDOWN = 5 * 60 * 1000

let lastPullAttempt = 0

function pullInBackground() {
  if (getSyncMode() !== 'online' || !isOnline() || !getToken()) return
  const now = Date.now()
  // Revenir sur l'app dix fois en deux minutes ne doit pas déclencher dix
  // requêtes : le délai vaut pour tous les déclencheurs, pas seulement le
  // retour d'onglet.
  if (now - lastPullAttempt < PULL_COOLDOWN) return
  lastPullAttempt = now
  // Même raison que `pushInBackground` : hors ligne, `fetch` lève, et un rejet
  // non traité remonterait au navigateur à chaque tentative.
  void pull().catch(() => { /* on retentera au prochain déclencheur */ })
}

/**
 * À appeler une fois l'appareil entré (voir `App.tsx`). Pas au chargement du
 * module : sans jeton, chaque déclencheur partirait se faire refuser en 401.
 */
export function startAutoPull(): void {
  lastPullAttempt = 0
  pullInBackground()
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    if (getSyncMode() !== 'online') return
    // Envoyer d'abord : ce qui a été saisi hors ligne est ce qu'on risque le
    // plus de perdre, et le pull ne doit jamais passer devant.
    pushInBackground()
    lastPullAttempt = 0
    pullInBackground()
  })

  // Le retour dans l'app, et non `focus` : sur un téléphone, revenir sur
  // l'onglet ou rouvrir l'app installée passe par là, pas par le focus fenêtre.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') pullInBackground()
  })
}
